import {
  UseQueryOptions,
  UseQueryResult,
  useQuery,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { FieldsAPI } from 'api/fields';
import { FieldsObjectTypeForApi } from 'api/fields/fields';
import { Field } from 'api/tasks';
import { useFilterPointsFieldsFromTaskFields } from 'hooks/use-filter-point-fields-from-task-fields';
import {
  ALL_FIELDS_QUERY_KEY,
  fieldsQueryKeys,
  objectSubTypes,
  objectTypes,
  oneDay,
  TASK_FIELDS_QUERY_KEY,
} from 'queries/constants';

type AccountFieldsQueryOptionType = UseQueryOptions<
  Field[],
  unknown,
  Field[],
  typeof TASK_FIELDS_QUERY_KEY
>;
type AccountFieldsQueryResultType = UseQueryResult<Field[], unknown>;

type QueryOptionType = UseQueryOptions<
  Field[],
  unknown,
  Field[],
  typeof ALL_FIELDS_QUERY_KEY
>;
type QueryResultType = UseQueryResult<Field[], unknown>;

export const useAllFieldsBaseQuery = (
  queryOptions: QueryOptionType = {},
): QueryResultType => {
  return useQuery(
    ALL_FIELDS_QUERY_KEY,
    async () => {
      return FieldsAPI.getAllFields().then((fields) =>
        fields.reduce((acc, field) => acc.concat(field.fields), [] as Field[]),
      );
    },
    {
      staleTime: oneDay,
      ...queryOptions,
    },
  );
};

export const useTaskFieldsBaseQuery = (
  queryOptions: AccountFieldsQueryOptionType = {},
): AccountFieldsQueryResultType => {
  const { filterPointsFieldsFromTaskFields } =
    useFilterPointsFieldsFromTaskFields();

  const selectWithPointsFilter = useCallback(
    (data: Field[]) => filterPointsFieldsFromTaskFields(data) as Field[],
    [filterPointsFieldsFromTaskFields],
  );

  return useQuery(
    TASK_FIELDS_QUERY_KEY,
    async () => {
      const resp = await FieldsAPI.getFieldsByObjectType(
        FieldsObjectTypeForApi.Tasks,
      );

      if (!resp?.fields?.length) {
        return [];
      }

      return resp.fields;
    },
    {
      ...queryOptions,
      select: selectWithPointsFilter,
    },
  );
};

export const useAllFieldsQueryV2 = (
  {
    objectType,
    objectSubType,
  }: {
    objectType: Field['objectType'];
    objectSubType?: Field['objectSubType'];
  },
  queryOptions: Omit<
    UseQueryOptions<Field[], unknown, Field[], string[]>,
    'queryKey' | 'queryFn'
  > = {},
) => {
  return useQuery(
    [...fieldsQueryKeys[objectType], objectSubType ?? 'DEFAULT'],
    async () => {
      const data = await FieldsAPI.getFieldsByObjectType(
        objectTypes[objectType],
        objectSubType ? objectSubTypes[objectSubType] : undefined,
      );

      return data?.fields ?? [];
    },
    queryOptions,
  );
};
